import { useObserva } from "@morganisbatman/observa-client-react";
import { Link, useLocalSearchParams } from "expo-router";
import { useEffect, useMemo, useState } from "react";
import { ActivityIndicator, StyleSheet, Text, View } from "react-native";

import { space, textStyle } from "@passfirst/ui";

import { Button } from "../../src/components/button";
import { Lockup } from "../../src/lockup";
import { type Colors, useColors } from "../../src/theme";
import { trpc } from "../../src/trpc";
import {
  useSelectedVertical,
  verticalDisplayLabel,
} from "../../src/use-selected-vertical";

export default function CheckoutSuccess(): JSX.Element {
  const observa = useObserva();
  const colors = useColors();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const selected = useSelectedVertical();
  const params = useLocalSearchParams<{
    session_id?: string;
    provider?: string;
    vertical?: string;
    jurisdiction?: string;
  }>();
  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "unlocked"; source: string }
    | { kind: "waiting" }
    | { kind: "error"; message: string }
  >({ kind: "loading" });

  const verticalParam =
    typeof params.vertical === "string" ? params.vertical : null;
  const jurisdictionParam =
    typeof params.jurisdiction === "string" ? params.jurisdiction : null;
  // Native IAP (apple-iap/google-iap) verifies the receipt and writes
  // entitlement server-side BEFORE navigating here, so there's no async
  // webhook to wait on — a single check confirms. The Stripe web flow
  // grants entitlement via an async webhook, so it polls.
  const isIap =
    params.provider === "apple-iap" || params.provider === "google-iap";

  // Friendly credential name for the confirmation, resolved from the
  // user's selection (falls back to undefined → generic copy).
  const credentialLabel = verticalDisplayLabel(selected);

  useEffect(() => {
    if (!verticalParam) {
      setState({
        kind: "error",
        message:
          "Missing vertical in success URL — checkout should pass ?vertical=<slug>",
      });
      return;
    }
    // `cancelled` guards against a state update after unmount / param
    // change.
    let cancelled = false;
    // The Stripe webhook that grants entitlement is asynchronous: the
    // user can land here a beat before checkout.session.completed has
    // been processed. A single check would then show "waiting" forever
    // even though access is seconds away. Poll a bounded number of times
    // with a short delay so the common case (webhook lands in 1–3s)
    // resolves to "unlocked" on its own. IAP entitlement is already
    // durable on arrival, so it checks exactly once.
    const ATTEMPTS = isIap ? 1 : 6;
    const DELAY_MS = 1500;
    const sleep = (ms: number) =>
      new Promise<void>((resolve) => setTimeout(resolve, ms));
    void (async () => {
      for (let attempt = 0; attempt < ATTEMPTS; attempt++) {
        try {
          const res = await trpc.entitlement.check.query({
            verticalSlug: verticalParam,
            jurisdictionCode: jurisdictionParam ?? undefined,
          });
          if (cancelled) return;
          if (res.hasAccess) {
            setState({ kind: "unlocked", source: res.source });
            return;
          }
        } catch (err) {
          if (cancelled) return;
          // Raw detail (tRPC codes, upstream messages) goes to observa —
          // the user only ever sees friendly recovery copy.
          void observa.error({
            error: err instanceof Error ? err : new Error("check failed"),
            context: {
              verticalSlug: verticalParam,
              jurisdictionCode: jurisdictionParam,
            },
          });
          setState({
            kind: "error",
            message:
              "We couldn't confirm your access just yet. Your payment went through — tap below to head home, and it'll appear shortly.",
          });
          return;
        }
        // Not yet entitled. Wait and retry unless this was the last
        // attempt, in which case fall through to the "waiting" state.
        if (attempt < ATTEMPTS - 1) await sleep(DELAY_MS);
      }
      if (!cancelled) setState({ kind: "waiting" });
    })();
    return () => {
      cancelled = true;
    };
  }, [verticalParam, jurisdictionParam, isIap]);

  // Emit once per resolved state. `unlocked` is the funnel-complete
  // signal (webhook landed before the user got here); `waiting` means
  // payment took but the webhook is in flight (analytics-interesting);
  // `error` means the entitlement check failed (likely missing
  // vertical param or backend issue).
  useEffect(() => {
    if (state.kind === "loading") return;
    void observa.event({
      eventType: "checkout.success_page_viewed",
      payload: {
        verticalSlug: verticalParam ?? "",
        jurisdictionCode: jurisdictionParam,
        entitlementState: state.kind,
      },
    });
  }, [state.kind, verticalParam, jurisdictionParam, observa]);

  return (
    <View style={styles.outer}>
      <View style={styles.topBar}>
        <Lockup size={26} />
      </View>
      <View style={styles.container}>
      <Text style={styles.title}>Checkout</Text>
      {/* Internal identifiers (Stripe session_id, mock-provider tag) are
          for debugging only — surface them in dev, never to end users. */}
      {__DEV__ && params.session_id ? (
        <Text style={styles.meta}>
          Session: {params.session_id}
          {params.provider === "mock" ? " (mock provider)" : ""}
        </Text>
      ) : null}

      {state.kind === "loading" ? (
        <ActivityIndicator accessibilityLabel="Checking entitlement" />
      ) : null}

      {state.kind === "unlocked" ? (
        <>
          <Text style={styles.ok} accessibilityLiveRegion="polite">
            You're all set ✓
          </Text>
          <Text style={styles.confirm}>
            {credentialLabel
              ? `Your full ${credentialLabel} prep is unlocked.`
              : "Your full prep is unlocked."}
          </Text>
          {/* `source` is an internal entitlement provenance string
              (e.g. "purchase", "family-share") — dev-only. */}
          {__DEV__ ? (
            <Text style={styles.meta}>source: {state.source}</Text>
          ) : null}
        </>
      ) : null}

      {state.kind === "waiting" ? (
        <Text style={styles.waiting} accessibilityLiveRegion="polite">
          {isIap
            ? "Payment received but entitlement not yet reflected. Pull to refresh or tap below to retry."
            : "Payment received but entitlement not yet reflected. If you're on real Stripe, the webhook may still be in flight."}
        </Text>
      ) : null}

      {state.kind === "error" ? (
        <Text style={styles.error} accessibilityLiveRegion="polite">
          {state.message}
        </Text>
      ) : null}

      <Link
        href={
          verticalParam
            ? {
                pathname: "/",
                params: {
                  vertical: verticalParam,
                  ...(jurisdictionParam
                    ? { jurisdiction: jurisdictionParam }
                    : {}),
                },
              }
            : "/"
        }
        asChild
      >
        <Button label="Back to home" />
      </Link>
      </View>
    </View>
  );
}

function makeStyles(colors: Colors) {
  return StyleSheet.create({
  outer: { flex: 1, backgroundColor: colors.background },
  topBar: {
    alignItems: "center",
    paddingTop: space[8],
    paddingBottom: space[2],
    borderBottomWidth: 1,
    borderBottomColor: colors.borderSoft,
  },
  container: {
    flex: 1,
    backgroundColor: colors.background,
    alignItems: "center",
    justifyContent: "center",
    padding: space[6],
    gap: space[4],
  },
  title: { ...textStyle.h2, color: colors.heading },
  meta: { ...textStyle.caption, color: colors.textMuted, fontFamily: "monospace" },
  ok: { ...textStyle.h3, color: colors.success },
  confirm: {
    ...textStyle.body,
    color: colors.textSecondary,
    textAlign: "center",
    maxWidth: 360,
  },
  waiting: { ...textStyle.bodySmall, color: colors.textMuted, textAlign: "center", maxWidth: 360 },
  error: { ...textStyle.bodySmall, color: colors.error, textAlign: "center" },
  });
}
